import { DAY_NAMES, MONTH_NAMES } from '@lib/accommodationConstants';
import { todayISO } from '@lib/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AccommodationBooking,
  AccommodationRoom,
} from '../../../types/accommodation';

interface Props {
  rooms: AccommodationRoom[];
  bookings: AccommodationBooking[];
  checkOutTime: string;
}

export const WeeklyCalendar: React.FC<Props> = ({
  rooms,
  bookings,
  checkOutTime,
}) => {
  const navigate = useNavigate();
  const [weekOffset, setWeekOffset] = useState(0);
  const todayStr = todayISO();

  const weekDays = useMemo(() => {
    const now = new Date();
    const startOfWeek = new Date(now);
    const dow = now.getDay();
    startOfWeek.setDate(
      now.getDate() - (dow === 0 ? 6 : dow - 1) + weekOffset * 7,
    );
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      return d;
    });
  }, [weekOffset]);

  const monthPivot = weekDays[3];
  const weekRange = `${weekDays[0].getDate()}–${weekDays[6].getDate()}`;
  const activeRooms = rooms.filter((r) => r.is_active);

  const bookingMap = useMemo(() => {
    const map = new Map<string, { guestName: string; bookingId: string }>();
    bookings.forEach((b) => {
      if (b.status === 'cancelled' || b.status === 'no_show') return;
      (b.booking_rooms ?? []).forEach((br) => {
        weekDays.forEach((d) => {
          const dayStr = d.toISOString().slice(0, 10);
          if (b.check_in <= dayStr && b.check_out > dayStr) {
            const guest = b.client
              ? `${b.client.first_name} ${b.client.last_name}`.trim()
              : `#${b.id.slice(0, 6).toUpperCase()}`;
            map.set(`${br.room_id}-${dayStr}`, {
              guestName: guest,
              bookingId: b.id,
            });
          }
        });
      });
    });
    return map;
  }, [bookings, weekDays]);

  return (
    <div className="flex h-[446px] flex-col rounded-2xl border border-gray-200 bg-white">
      {/* Header */}
      <div className="border-b border-gray-100 px-4 py-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-900">Calendar</h4>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-500">
            Check-out {checkOutTime.slice(0, 5)}
          </span>
        </div>
        <p className="mt-0.5 text-xs text-gray-400">
          {activeRooms.length} room{activeRooms.length !== 1 ? 's' : ''}{' '}
          overview
        </p>
      </div>

      {/* Week navigation */}
      <div className="flex items-center justify-between border-b border-gray-50 px-4 py-2">
        <button
          type="button"
          onClick={() => setWeekOffset((w) => w - 1)}
          className="rounded-lg p-1 text-gray-400 hover:bg-gray-100"
          aria-label="Previous week"
        >
          <ChevronLeft size={14} />
        </button>
        <p className="text-xs font-semibold text-gray-700">
          {MONTH_NAMES[monthPivot.getMonth()]} {monthPivot.getFullYear()} (
          {weekRange})
        </p>
        <button
          type="button"
          onClick={() => setWeekOffset((w) => w + 1)}
          className="rounded-lg p-1 text-gray-400 hover:bg-gray-100"
          aria-label="Next week"
        >
          <ChevronRight size={14} />
        </button>
      </div>

      {/* Day header row */}
      <div className="grid grid-cols-[64px_repeat(7,1fr)] border-b border-gray-50 px-2 py-1.5">
        <div />
        {weekDays.map((d) => {
          const iso = d.toISOString().slice(0, 10);
          const isToday = iso === todayStr;
          return (
            <div key={iso} className="text-center">
              <p
                className={`text-[10px] font-semibold ${isToday ? 'text-pos-primary' : 'text-gray-400'}`}
              >
                {String(d.getDate()).padStart(2, '0')}
              </p>
              <p
                className={`text-[9px] ${isToday ? 'text-pos-primary' : 'text-gray-300'}`}
              >
                {DAY_NAMES[d.getDay()]}
              </p>
              {isToday && (
                <div className="mx-auto mt-0.5 h-1 w-1 rounded-full bg-pos-primary" />
              )}
            </div>
          );
        })}
      </div>

      {/* Room rows */}
      <div className="flex-1 overflow-y-auto px-2 py-1">
        {activeRooms.length === 0 ? (
          <p className="py-4 text-center text-xs text-gray-400">
            No active rooms
          </p>
        ) : (
          activeRooms.map((room) => (
            <div
              key={room.id}
              className="grid grid-cols-[64px_repeat(7,1fr)] border-b border-gray-50 py-1.5"
            >
              <div className="flex flex-col justify-center">
                <p className="text-[10px] font-semibold text-gray-700">
                  #{room.room_number}
                </p>
                <p className="text-[9px] text-gray-400">
                  {room.room_type?.name ?? ''}
                </p>
              </div>
              {weekDays.map((d, dayIdx) => {
                const iso = d.toISOString().slice(0, 10);
                const entry = bookingMap.get(`${room.id}-${iso}`);
                const prevIso =
                  dayIdx > 0
                    ? weekDays[dayIdx - 1].toISOString().slice(0, 10)
                    : null;
                const prevEntry = prevIso
                  ? bookingMap.get(`${room.id}-${prevIso}`)
                  : null;
                const isStart =
                  entry &&
                  (!prevEntry || prevEntry.bookingId !== entry.bookingId);
                return (
                  <div
                    key={iso}
                    className={`relative mx-px h-8 rounded ${entry ? 'bg-pos-primary/15' : ''}`}
                  >
                    {isStart && (
                      <button
                        type="button"
                        onClick={() =>
                          navigate(`/accommodation/bookings/${entry.bookingId}`)
                        }
                        className="absolute inset-0 flex items-center truncate pl-1 text-[9px] font-medium text-pos-primary hover:text-pos-primary/70 w-full text-left"
                      >
                        {entry.guestName.split(' ')[0]}
                      </button>
                    )}
                    {!entry && (
                      <div className="flex h-full items-center justify-center">
                        <div className="h-px w-3/4 bg-gray-100" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>
    </div>
  );
};
